<template>
  <main>
    <AppBarNotification />
    <SnackBarNotification />
    <Store />
    <CartDrawer v-if="$vuetify.breakpoint.smAndUp" />
    <IdRequiredDialog />
    <MinimumOrderAmountDialog />
    <ProductAddFromCartDialog />
    <ProductDetailsDialog v-if="$vuetify.breakpoint.smAndUp" />
    <ProductModifiersDialog v-if="$vuetify.breakpoint.smAndUp" />
  </main>
</template>

<script>
import AppBarNotification from "@/components/Notifications/AppBar.vue";
import CartDrawer from "@/components/Cart/Drawer.vue";
import IdRequiredDialog from "@/components/Cart/Dialogs/IdRequiredDialog.vue";
import MinimumOrderAmountDialog from "@/components/Order/Dialogs/MinimumOrderAmount.vue";
import ProductAddFromCartDialog from "@/components/Product/Dialogs/AddFromCart.vue";
import ProductDetailsDialog from "@/components/Product/Dialogs/Details.vue";
import ProductModifiersDialog from "@/components/Product/Dialogs/Modifiers.vue";
import SnackBarNotification from "@/components/Notifications/SnackBar.vue";
import Store from "@/components/Store/Store.vue";

export default {
  name: "AppStore",
  components: {
    AppBarNotification,
    CartDrawer,
    IdRequiredDialog,
    MinimumOrderAmountDialog,
    ProductAddFromCartDialog,
    ProductDetailsDialog,
    ProductModifiersDialog,
    SnackBarNotification,
    Store,
  },
};
</script>
<style>
/*
https://type-scale.com/

this is the idea for responsive font sizing
then the rem will be resized along with base body font size

body {
  font-size: 16px;
}
@media (min-width: 668px) {
  body {
    font-size: 18px;
  }
}
@media (min-width: 960px) {
  body {
    font-size: 20px;
  }
}
@media (min-width: 1264px) {
  body {
    font-size: 24px;
  }
}
@media (min-width: 1904px) {
  body {
    font-size: 26px;
  }
}
*/

:root {
  --v-primary-base: #530d69; /* --v-primary-base */
  --v-primary-lighten1: #66307e; /* --v-primary-base */
  --color-seashell: #f6f5f4; /* --v-secondary-base */
  --color-green: #41b029; /* --v-secondary-base */
  /**/
  --color-black-magic: #2a2825;
  --color-dim-gray: #707070;
  /* one shade darker than suva-grey */
  --color-fire-engine-red: #e21b23;
  --color-gainsboro: #dadada;
  --color-lavender-grey: #dbdad9;
  --color-nobel: #969696;
  /* one shade lighter than suva-grey */
  --color-prelude: #c7afd1;
  --color-suva-grey: #8a8a8a;
  --color-white: #ffffff;

  --font-size-lg: 1.375rem;
  /* 22px */
  --font-size-md: 1rem;
  /* 16px */
  --font-size-sm: 0.875rem;
  /* 14px */
  --font-size-xl: 1.625rem;
  /* 26px */
  --font-size-xs: 0.75rem;
  /* 12px */
  --font-size-xxl: 1.875rem;
  /* 30px */
  --font-size-xxs: 0.65rem;
  /* 8px */
  --font-weight-semibold: 600;
}

* {
  -webkit-font-smoothing: antialiased;
}

a {
  text-decoration: inherit;
}

.alert-height {
  height: 80px;
}

.background-color-fire-engine-red {
  background-color: var(--color-fire-engine-red);
}

.background-color-fire-engine-red-trans {
  background: rgba(226, 27, 35, 0.2);
}

.background-primary-color {
  background-color: var(--v-primary-base);
}
.background-secondary-color {
  background-color: var(--v-secondary-base);
}

.background-color-lavender-grey {
  background-color: var(--color-lavender-grey);
}

.background-color-prelude {
  background-color: var(--color-prelude);
}

.background-color-seashell {
  background-color: var(--color-seashell);
}

.background-color-white {
  background-color: var(--color-white);
}

.background-color-green {
  background-color: var(--color-green);
}

.border-bottom {
  border-bottom: 1px solid #dadada;
}

.border-top {
  border-top: 1px solid #dadada;
}

.border-top-not-first-child:not(:first-child) {
  border-top: 1px solid #dadada;
}

.cursor-pointer {
  cursor: pointer;
}

.color-black-magic {
  color: var(--color-black-magic);
}

.color-dim-gray {
  color: var(--color-dim-gray);
}

.color-fire-engine-red {
  color: var(--color-fire-engine-red);
}

.color-gainsboro {
  color: var(--color-gainsboro);
}

.primary-color {
  color: var(--v-primary-base);
}

.color-nobel {
  color: var(--color-nobel);
}

.color-suva-grey {
  color: var(--color-suva-grey);
}

.color-white {
  color: var(--color-white);
}

.dont-break-out {
  hyphens: auto;
  -moz-hyphens: auto;
  -ms-hyphens: auto;
  -webkit-hyphens: auto;
  overflow-wrap: break-word;
  word-wrap: break-word;
  word-break: break-all;
  word-break: normal;
}

.flex-1 {
  flex: 1;
}

.font-size-lg-res {
  font-size: var(--font-size-md);
}

@media (min-width: 668px) {
  .font-size-lg-res {
    font-size: var(--font-size-lg);
  }
}

.font-size-md-res {
  font-size: var(--font-size-xs);
}

@media (min-width: 668px) {
  .font-size-md-res {
    font-size: var(--font-size-sm);
  }
}

@media (min-width: 1264px) {
  .font-size-md-res {
    font-size: var(--font-size-md);
  }
}

.font-size-sm-res {
  font-size: var(--font-size-xs);
}

@media (min-width: 1264px) {
  .font-size-sm-res {
    font-size: var(--font-size-sm);
  }
}

.font-size-xs-res {
  font-size: var(--font-size-xxs);
}

@media (min-width: 668px) {
  .font-size-xs-res {
    font-size: var(--font-size-xs);
  }
}

.font-size-lg {
  font-size: var(--font-size-lg);
}

.font-size-md {
  font-size: var(--font-size-md);
}

.font-size-sm {
  font-size: var(--font-size-sm);
}

.font-size-xl {
  font-size: var(--font-size-xl);
}

.font-size-xs {
  font-size: var(--font-size-xs);
}

.font-size-xxl {
  font-size: var(--font-size-xxl);
}

.font-size-xxs {
  font-size: var(--font-size-xxs);
}

.font-weight-semibold {
  font-weight: var(--font-weight-semibold);
}

.full-height {
  height: 100%;
}

.full-width {
  width: 100%;
}

.height-100vh {
  height: 100vh;
}

.hidden {
  visibility: hidden;
}

.image-container {
  overflow: hidden;
  position: relative;
}

.input-styles {
  border-bottom-right-radius: 10px;
  border-top-right-radius: 10px;
  border-bottom-left-radius: 10px;
  border-top-left-radius: 10px;
}

.line-height-1 {
  line-height: 1.1;
}

.nav-button-border-radius {
  border-radius: 6px;
}

.no-overflow {
  overflow-y: auto;
}

.no-uppercase {
  text-transform: none;
}

.outline-none {
  outline: none;
}

.pac-container:after {
  /* Disclaimer: not needed to show 'powered by Google' if also a Google Map is shown */
  background-image: none !important;
  height: 0px;
}

.position-absolute {
  position: absolute;
}

.position-relative {
  position: relative;
}

.product-image-size {
  object-fit: contain;
  height: 160px;
  width: 160px;
}

.product-image-size-lg {
  object-fit: contain;
  height: 250px;
  width: 250px;
}

.product-image-size-sm {
  object-fit: contain;
  /*
  height: 140px;
  width: 140px;
  */
  height: 223px;
  width: 223px;
}

.product-image-size-xs {
  object-fit: contain;
  height: 80px;
  width: 80px;
}

.quantity-display-width {
  width: 42px;
}

.side-nav-image-size {
  height: 24px;
  width: 24px;
}

.swiper {
  z-index: auto !important;
}

.swiper-wrapper {
  z-index: auto !important;
}

.text-decoration-underline {
  text-decoration: underline;
}

.vertical-align-middle {
  vertical-align: middle;
}

.v-btn--disabled {
  /*
   * https://github.com/vuetifyjs/vuetify/issues/13599
   * fixes issue in safari where button font color doesn't
   * change when toggling to/from disabled state
   */
  pointer-events: unset !important;
  cursor: default;
}

table {
  border-collapse: collapse;
  border-spacing: 0;
  margin: 0;
  padding: 10px;
  border: 0;
  font-size: 100%;
  font: inherit;
  vertical-align: baseline;
}

ul {
  list-style: none;
  padding-left: 0;
}

.vertical-chip {
  border-radius: 22px;
  padding: 6px 0;
}

.visible {
  visibility: visible;
}

.white-space-pre {
  white-space: pre;
}

.white-space-pre-wrap {
  white-space: pre-wrap;
}

.z-index-1 {
  z-index: 1;
}
</style>
